<template>
  <div id="draggable-container" ref="draggableContainer">
    <div id="draggable-header" @mousedown="dragMouseDown">
      <slot name="header" />
    </div>
    <slot name="content" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

const emits = defineEmits<{
  (e: 'newPosition', position: { left: string; top: string })
}>()

const props = defineProps<{
  background?: string
  startPosition?: { left: string; top: string }
}>()
const draggableContainer = ref()
const positions = ref({
  clientX: 0,
  clientY: 0,
  movementX: 0,
  movementY: 0
})

const backgroundProp = computed<string>(() => props.background ?? 'white')
const startPosition = computed<{ left: string; top: string } | undefined>(() => props.startPosition)

function closeDragElement() {
  emits('newPosition', {
    left: draggableContainer.value.style.left,
    top: draggableContainer.value.style.top
  })
  document.onmouseup = null
  document.onmousemove = null
}
function dragMouseDown(event) {
  event.preventDefault()
  event.stopPropagation()
  // get the mouse cursor position at startup:
  positions.value.clientX = event.clientX
  positions.value.clientY = event.clientY
  document.onmousemove = elementDrag
  document.onmouseup = closeDragElement
}
function elementDrag(event) {
  event.preventDefault()
  const containerWidth = draggableContainer.value.clientWidth
  const containerHeight = draggableContainer.value.clientHeight
  const edgePadding = 8
  const maxX = window.innerWidth - containerWidth - edgePadding
  const maxY = window.innerHeight - containerHeight - edgePadding
  positions.value.movementX = positions.value.clientX - event.clientX
  positions.value.movementY = positions.value.clientY - event.clientY
  positions.value.clientX = event.clientX
  positions.value.clientY = event.clientY
  const newLeft = draggableContainer.value.offsetLeft - positions.value.movementX
  const newTop = draggableContainer.value.offsetTop - positions.value.movementY
  if (newLeft > edgePadding && newLeft <= maxX) draggableContainer.value.style.left = newLeft + 'px'
  if (newTop > edgePadding && newTop <= maxY) draggableContainer.value.style.top = newTop + 'px'
}
watch(startPosition, (newValue) => {
  if (newValue) {
    draggableContainer.value.style.left = newValue.left
    draggableContainer.value.style.top = newValue.top
  }
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
#draggable-container {
  background: v-bind(backgroundProp);
  border-radius: 8px;
  box-shadow: variables.$box-shadow-small;
  display: flex;
  flex-direction: column;
  position: fixed;
  width: fit-content;
  z-index: 1011;
}
#draggable-header {
  background-color: variables.$color-primary;
  border-radius: 8px 8px 0 0;
  cursor: all-scroll;
  display: flex;
  height: 20px;
  width: 100%;
  z-index: 1012;
}
</style>
